import {
  OrderBookConfigurationKeys,
  OrderBookConfigurations,
  ORDER_BOOK_NAMESPACE,
  PAGINATION_CONFIG,
} from '@epicuro-next/data/order-book';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { isNonNullish } from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';

import { getUniqueOrderId } from '../shared/get-unique-order-id/get-unique-order-id';
import { selectOrderBookState } from '../store';

import { ordersFeatureKey } from './orders.reducer';

export const selectOrdersRootState = createSelector(
  selectOrderBookState,
  (state) => state[ordersFeatureKey],
);

export const selectOrdersState = createSelector(
  selectOrdersRootState,
  (state) => state.orders,
);

export const selectExportState = createSelector(
  selectOrdersRootState,
  (state) => state.exportOrders,
);

export const selectOrders = createSelector(
  selectOrdersState,
  (orders) => orders.data,
);

export const selectTotals = createSelector(
  selectOrdersState,
  (orders) => orders.totals,
);

export const selectNoOrdersSearchResult = createSelector(
  selectOrders,
  (orders) => isNonNullish(orders) && orders.length === 0,
);

export const selectSelectedOrderIds = createSelector(
  selectOrdersRootState,
  (state) => state.selectedIds,
);

export const selectSelectedOrders = createSelector(
  selectSelectedOrderIds,
  selectOrders,
  (selectedIds, orders) =>
    selectedIds
      .map((id) => orders?.find((order) => getUniqueOrderId(order) === id))
      .filter(isNonNullish),
);

export const selectIsAnyOrderSelected = createSelector(
  selectSelectedOrderIds,
  (selectedIds) => !!selectedIds.length,
);

export const selectIsOrderSelected = (id: string) =>
  createSelector(selectSelectedOrderIds, (selectedIds) =>
    selectedIds.some((selectedId) => selectedId === id),
  );

export const selectIsOrdersLoading = createSelector(
  selectOrdersState,
  (orders) => orders.loading,
);

export const selectLoadOrdersError = createSelector(
  selectOrdersState,
  (orders) => orders.error,
);

export const selectIsExportLoading = createSelector(
  selectExportState,
  (exportOrders) => exportOrders.loading,
);

export const selectIsExportAllLoading = createSelector(
  selectExportState,
  (exportOrders) => exportOrders.exportingAllOrders,
);

export const selectLoadExportError = createSelector(
  selectExportState,
  (exportOrders) => exportOrders.error,
);

export const selectOrdersCurrency = createSelector(
  selectOrders,
  (orders) => (orders ?? [])[0]?.unitPriceNet?.currency ?? null,
);

export const selectShowOrdersAmount = selectConfigurationValueFrom<
  OrderBookConfigurationKeys.showOrdersAmount,
  OrderBookConfigurations
>(ORDER_BOOK_NAMESPACE, OrderBookConfigurationKeys.showOrdersAmount, false);

export const selectOrderBookPageSize = selectConfigurationValueFrom<
  OrderBookConfigurationKeys.paginationPageSize,
  OrderBookConfigurations
>(
  PAGINATION_CONFIG.namespace,
  OrderBookConfigurationKeys.paginationPageSize,
  PAGINATION_CONFIG.defaultValue,
);

export const selectPaginationAfterKey = createSelector(
  selectOrdersState,
  (state) => state.afterKey,
);

export const selectPaginationFilter = createSelector(
  selectPaginationAfterKey,
  selectOrderBookPageSize,
  (afterKey, amount) => {
    return {
      size: amount,
      afterKey,
    };
  },
);

export const selectOrderBookPaginationLoading = createSelector(
  selectOrdersState,
  (state) => state.paginationLoading,
);

export const selectHasMoreOrders = createSelector(selectOrdersState, (state) =>
  Boolean(state.afterKey),
);

export const selectCanLoadMoreOrders = createSelector(
  selectHasMoreOrders,
  selectOrderBookPaginationLoading,
  (hasMorePages, paginationLoading) => hasMorePages && !paginationLoading,
);

export const selectPaginationTotal = createSelector(
  selectOrdersState,
  (state) => state.total,
);
